// IndexedDB-backed store for photos that have been captured but not yet
// uploaded. Lets capture return instantly even on bad cell reception
// while the upload queue (photoUploadQueue.ts) drains the backlog in
// the background.
//
// On-disk shape stores the photo bytes as ArrayBuffer (not Blob) to
// dodge iOS Safari's well-known IndexedDB Blob retrieval bug, where
// blobs come back as "valid Blob objects" but their underlying data
// is unreadable (image decode fails with "Could not load image").
// Callers always get/put Blobs; the (de)serialization happens here.

export type PendingPhotoStatus = 'pending' | 'uploading' | 'failed'

export interface PendingPhoto {
  local_id: string          // client-generated UUID; also becomes server photo.id on upload
  project_id: string
  user_id: string
  photographer_name: string | null  // denormalized at capture; survives user rename/delete
  original_blob: Blob
  captured_at: string       // ISO timestamp client-side
  latitude: number | null
  longitude: number | null
  is_hd: boolean
  status: PendingPhotoStatus
  attempts: number          // failed pipeline runs in the current revival cycle
  revivals: number          // times resumeQueue has auto-revived after a 'failed' — budget-capped
  last_error: string | null
  enqueued_at: number       // ms epoch; for FIFO ordering
}

// Internal on-disk row. Same shape as PendingPhoto but with the blob
// flattened to ArrayBuffer + MIME type so iOS Safari serializes the
// bytes reliably.
interface StoredPhoto extends Omit<PendingPhoto, 'original_blob'> {
  original_buffer: ArrayBuffer
  original_type: string
}

async function toStored(p: PendingPhoto): Promise<StoredPhoto> {
  const { original_blob, ...rest } = p
  return {
    ...rest,
    original_buffer: await original_blob.arrayBuffer(),
    original_type: original_blob.type || 'image/jpeg',
  }
}

function fromStored(s: StoredPhoto): PendingPhoto {
  const { original_buffer, original_type, ...rest } = s
  return {
    ...rest,
    original_blob: new Blob([original_buffer], { type: original_type }),
  }
}

const DB_NAME = 'punch-pending-photos'
// v2 — switched on-disk shape from Blob to ArrayBuffer to dodge iOS
// Safari's IDB Blob bug (see header comment). Any v1 entries are
// effectively unrecoverable (their Blob data is gone), so the upgrade
// path drops the store and recreates it.
const DB_VERSION = 2
const STORE = 'photos'

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (event) => {
      const db = req.result
      // v1 → v2: discard the old store. Entries there have
      // unrecoverable Blob bodies on iOS Safari.
      if (event.oldVersion < 2 && db.objectStoreNames.contains(STORE)) {
        db.deleteObjectStore(STORE)
      }
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'local_id' })
        os.createIndex('by_project', 'project_id', { unique: false })
        os.createIndex('by_status', 'status', { unique: false })
        os.createIndex('by_enqueued_at', 'enqueued_at', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then(db => db.transaction(STORE, mode).objectStore(STORE))
}

// ── subscribers ──────────────────────────────────────────────────────────────
// IDB doesn't push events; we maintain our own pub/sub. Anything that mutates
// the store calls notify() so React hooks can re-read.

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

function notify() {
  for (const fn of listeners) {
    try { fn() } catch (err) { console.error('pendingPhotoStore listener error:', err) }
  }
}

// ── reads ────────────────────────────────────────────────────────────────────

export async function getAll(): Promise<PendingPhoto[]> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.getAll()
    req.onsuccess = () => {
      const stored = req.result as StoredPhoto[]
      const decoded = stored.map(fromStored).sort((a, b) => a.enqueued_at - b.enqueued_at)
      resolve(decoded)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function getByProject(projectId: string): Promise<PendingPhoto[]> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.index('by_project').getAll(projectId)
    req.onsuccess = () => {
      const stored = req.result as StoredPhoto[]
      const decoded = stored.map(fromStored).sort((a, b) => a.enqueued_at - b.enqueued_at)
      resolve(decoded)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function getNextPending(): Promise<PendingPhoto | null> {
  // FIFO by enqueued_at, only status='pending'.
  const all = await getAll()
  return all.find(p => p.status === 'pending') ?? null
}

export async function get(localId: string): Promise<PendingPhoto | null> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.get(localId)
    req.onsuccess = () => {
      const s = req.result as StoredPhoto | undefined
      resolve(s ? fromStored(s) : null)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function count(): Promise<number> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.count()
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

// ── writes ───────────────────────────────────────────────────────────────────

export async function put(photo: PendingPhoto): Promise<void> {
  const stored = await toStored(photo)
  const store = await tx('readwrite')
  await new Promise<void>((resolve, reject) => {
    const req = store.put(stored)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
  notify()
}

export async function update(localId: string, patch: Partial<PendingPhoto>): Promise<void> {
  const existing = await get(localId)
  if (!existing) return
  await put({ ...existing, ...patch })
}

export async function remove(localId: string): Promise<void> {
  const store = await tx('readwrite')
  await new Promise<void>((resolve, reject) => {
    const req = store.delete(localId)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
  notify()
}

// ── lifecycle helpers ────────────────────────────────────────────────────────

// On app boot, any entry left in 'uploading' state was interrupted (page
// reload, crash) — mark it back to 'pending' so the queue picks it up.
export async function recoverInterrupted(): Promise<number> {
  const all = await getAll()
  const interrupted = all.filter(p => p.status === 'uploading')
  for (const p of interrupted) {
    await update(p.local_id, { status: 'pending' })
  }
  return interrupted.length
}
